import React, { ReactElement } from "react";

import { Toolbar, ToolbarButton, ToolbarMenu, ToolbarTool } from "~/core";
import { IconType, ToolbarIcon } from "../../assets/toolbar-icon";
import { DrawTools, GeometryMath, MapConfig } from "@ai360/core";
import { logFirebaseEvent } from "~/utils/firebase";

export class DrawToolSet extends DrawTools {
    getToolbar(
        disabled = false,
        includeClipTool = false,
        includeShrinkwrapTool = false
    ): ReactElement {
        disabled = disabled || !this.customerClipBoundary;
        const { formatMessage } = this.options;
        this.includeClipTool = includeClipTool;
        this.includeShrinkwrapTool = includeShrinkwrapTool;

        const toolsDisabled = this.mapView.scale > MapConfig.layers.fields.minScale;

        if ((disabled || toolsDisabled) !== this.disabled) {
            this.toggleTool({ isRenderRequest: true, selected: false }, false);
        }
        this.disabled = disabled;

        const tools = [];
        tools.push(
            <ToolbarTool
                key={0}
                disabled={toolsDisabled}
                icon={ToolbarIcon(IconType.POLYGON)}
                label={formatMessage(this.messages.drawPolygonLbl)}
                onToggle={(evt) => {
                    logFirebaseEvent("draw_polygon");
                    this.toggleTool(evt);
                }}
                ref={(tool) => (this.drawPolygon = tool)}
            />,
            <ToolbarTool
                key={1}
                disabled={toolsDisabled}
                icon={ToolbarIcon(IconType.CIRCLE)}
                label={formatMessage(this.messages.drawCircleLbl)}
                onToggle={(evt) => {
                    logFirebaseEvent("draw_circle");
                    this.toggleTool(evt);
                }}
                ref={(tool) => (this.drawCircle = tool)}
            />
        );
        if (this.includeShrinkwrapTool) {
            tools.push(
                <ToolbarTool
                    key={2}
                    disabled={toolsDisabled}
                    icon={ToolbarIcon(IconType.SHRINKWRAP)}
                    label={formatMessage(this.messages.drawShrinkwrapLbl)}
                    onToggle={(evt) => {
                        logFirebaseEvent("draw_shrinkwrap");
                        this.toggleTool(evt);
                    }}
                    ref={(tool) => (this.drawShrinkwrap = tool)}
                />
            );
        }
        tools.push(
            <ToolbarTool
                key={this.includeShrinkwrapTool ? 3 : 2}
                disabled={toolsDisabled || this.geometries.length === 0}
                icon={ToolbarIcon(IconType.RESHAPE)}
                label={formatMessage(this.messages.drawReshapeLbl)}
                onToggle={(evt) => {
                    logFirebaseEvent("draw_reshape");
                    this.toggleTool(evt);
                }}
                ref={(tool) => (this.drawReshape = tool)}
            />,
            <ToolbarTool
                key={this.includeShrinkwrapTool ? 4 : 3}
                disabled={toolsDisabled || this.geometries.length === 0}
                icon={ToolbarIcon(IconType.SPLIT)}
                label={formatMessage(this.messages.drawSplitLbl)}
                onToggle={(evt) => {
                    logFirebaseEvent("draw_split");
                    this.toggleTool(evt);
                }}
                ref={(tool) => (this.drawSplit = tool)}
            />,
            <ToolbarTool
                key={this.includeShrinkwrapTool ? 5 : 4}
                disabled={toolsDisabled || this.geometries.length === 0}
                icon={ToolbarIcon(IconType.MOVE)}
                label={formatMessage(this.messages.drawNudgeLbl)}
                onToggle={(evt) => {
                    logFirebaseEvent("draw_nudge");
                    this.toggleTool(evt);
                }}
                ref={(tool) => (this.drawNudge = tool)}
            />,
            <ToolbarTool
                key={this.includeShrinkwrapTool ? 6 : 5}
                disabled={
                    toolsDisabled ||
                    this.selectedGeometry == null ||
                    GeometryMath.getTotalPolygonPartCount(this.geometries) <= 1
                }
                icon={ToolbarIcon(IconType.UNION)}
                label={formatMessage(this.messages.drawUnionLbl)}
                onToggle={(evt) => {
                    logFirebaseEvent("draw_union");
                    this.toggleTool(evt);
                }}
                ref={(tool) => (this.drawUnion = tool)}
            />
        );
        if (this.includeClipTool) {
            tools.push(
                <ToolbarButton
                    key={this.includeShrinkwrapTool ? 7 : 6}
                    disabled={toolsDisabled || !this._canClip()}
                    icon={ToolbarIcon(IconType.CLIP)}
                    label={formatMessage(this.messages.drawClipLbl)}
                    onClick={() => this.clip()}
                />
            );
        }

        return (
            <ToolbarMenu
                key="draw"
                className="draw"
                icon={ToolbarIcon(IconType.DRAW)}
                disabled={this.disabled}
                label={formatMessage(this.messages.drawToolsLbl)}
                ref={(menu) => (this.menu = menu)}
            >
                <Toolbar className="draw-tools">{tools}</Toolbar>
            </ToolbarMenu>
        );
    }
}
